import { Component, EventEmitter, inject, Output, ViewChild } from '@angular/core';
import { DialogComponent } from 'src/app/controls/dialog/components/dialog.component';
import { MatFormFieldModule } from '@angular/material/form-field';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { RoundService } from './services/round.service';
import { Round } from './models/round.interface';
import { FormsModule } from '@angular/forms';
import { SnackBarService } from 'src/app/controls/snack-bar/services/snack-bar.service';
import { MatInputModule } from '@angular/material/input';
import { RoundData } from './models/round-data.interface';

@Component({
  selector: 'cec-round-dialog',
  templateUrl: './round-dialog.component.html',
  standalone: true,
  imports: [DialogComponent, MatFormFieldModule, TranslateModule, FormsModule, MatInputModule],
  providers: [RoundService]
})
export class RoundDialogComponent {

  /** Dialog component reference to access */
  @ViewChild(DialogComponent) 
  protected dialog!: DialogComponent;

  /** Round name value reference */
  protected name: string | undefined = undefined;

  /** Round id reference */
  protected id: number | undefined = undefined;

  /** Competition id reference */
  private competitionId: number | undefined = undefined;

  /** Round service reference */
  private service = inject(RoundService);

  /** Snackbar service reference */
  private snackBar = inject(SnackBarService);

  /** Translate service reference */
  private translate = inject(TranslateService);

  /** Refresh competition card emitter */
  @Output() refresh = new EventEmitter<void>();

  /**
   * Shows the round dialog
   * @param competitionId the id of competition
   * @param id the id of round
   */
  public show(competitionId: number, id?: number, name?: string): void {
    this.competitionId = competitionId;
    this.id = id;
    this.name = name;
    this.dialog.show();
  }

  /**
   * Returns the button click success
   * @param success the button click success
   */
  protected onClickButton(success: boolean): void {
    /** if cancel button was clicked, resets the name attribute and hides the dialog */
    if (!success) {
      this.name = undefined;
      this.dialog.hide();
    }

    /** if name is not present, do nothing */
    if (!this.name) {
      return;
    }

    /** builds round with known data */
    const round: RoundData = {
      id: this.id,
      name: this.name,
      competitionId: this.competitionId
    }

    if (this.id) {
      /** if id is present, updates an existing round */
      this.service.update(round).subscribe((success: boolean) => {
        this.name = undefined;
        if (success) {
          /** shows success snackbar when existing round is updated */
          this.snackBar.success(this.translate.instant('RoundUpdateSuccess'));

          /** refreshes the competition card */
          this.refresh.emit();
          return;
        }

        /** shows danger snackbar when update of round fails */
        this.snackBar.danger(this.translate.instant('RoundUpdateFailed'));
      });
    } else {
      /** if id is not present, create a new round */
      this.service.create(round).subscribe((success: boolean) => {
        this.name = undefined;
        if (success) {
          /** shows success snackbar when new round is created */
          this.snackBar.success(this.translate.instant('RoundCreateSuccess'));

          /** refreshes the competition card */
          this.refresh.emit();
          return;
        }

        /** shows danger snackbar when creation of round fails */
        this.snackBar.danger(this.translate.instant('RoundCreateFailed'));
      });
    }
  }

}
